import React, { useState, useEffect } from 'react'
import { Cable, Plug, Webhook, Boxes } from 'lucide-react'
import SyncManager from './SyncManager'
import Integrations from './Integrations'
import AdapterManager from './AdapterManager'
import AgentAsk from '../components/AgentAsk'

// Connections — a single hub that gathers every system that feeds or receives
// your data behind one tab bar. Each tab simply mounts an existing page component
// as a panel (they bring their own content and data wiring); this file only
// supplies the header, the tab chrome, and tab persistence.
//
// VISUAL LAYER ONLY: the flat "Nunito" aesthetic — soft palette, rounded cards,
// pill tabs, light+dark theme tokens — mirrors GrowthLab and is scoped under
// `.cn-root` (styles prefixed `.cn-*`) so nothing leaks into the mounted panels.

const CN_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800&display=swap');
.cn-root{
  --cn-bg:#FBFBF7; --cn-panel:#FFFFFF; --cn-panel2:#F6F6F0; --cn-line:#ECEBE3;
  --cn-ink:#2E3A45; --cn-mut:#7C8794; --cn-dim:#AEB6BD; --cn-accent:#4E9AC4;
  --cn-shadow:0 1px 2px rgba(46,58,69,.05),0 10px 26px rgba(46,58,69,.07);
  font-family:Nunito,system-ui,-apple-system,Segoe UI,sans-serif;
  color:var(--cn-ink); -webkit-font-smoothing:antialiased;
}
[data-theme="dark"] .cn-root{
  --cn-bg:#0E1116; --cn-panel:#171B22; --cn-panel2:#12161C; --cn-line:#262C36;
  --cn-ink:#E7ECF1; --cn-mut:#95A0AD; --cn-dim:#5C6672; --cn-accent:#5FB0DB;
  --cn-shadow:0 1px 2px rgba(0,0,0,.3),0 10px 26px rgba(0,0,0,.4);
}
@media (prefers-color-scheme:dark){:root:not([data-theme="light"]) .cn-root{
  --cn-bg:#0E1116; --cn-panel:#171B22; --cn-panel2:#12161C; --cn-line:#262C36;
  --cn-ink:#E7ECF1; --cn-mut:#95A0AD; --cn-dim:#5C6672; --cn-accent:#5FB0DB;
  --cn-shadow:0 1px 2px rgba(0,0,0,.3),0 10px 26px rgba(0,0,0,.4);
}}
.cn-root *{box-sizing:border-box;}
.cn-title{font-size:24px;font-weight:800;letter-spacing:-.02em;margin:0;display:flex;align-items:center;gap:10px;color:var(--cn-ink);}
.cn-sub{margin:4px 0 0;color:var(--cn-mut);font-size:14px;font-weight:500;line-height:1.5;}
.cn-tabs{display:inline-flex;gap:4px;padding:5px;border-radius:14px;background:var(--cn-panel2);border:1px solid var(--cn-line);flex-wrap:wrap;}
.cn-tab{font-family:Nunito,system-ui,sans-serif;font-size:13px;font-weight:800;border-radius:10px;padding:9px 15px;cursor:pointer;display:inline-flex;align-items:center;gap:7px;border:1px solid transparent;background:none;color:var(--cn-mut);transition:.16s;}
.cn-tab:hover{color:var(--cn-ink);}
.cn-tab.active{background:var(--cn-panel);color:var(--cn-ink);border-color:var(--cn-line);box-shadow:var(--cn-shadow);}
.cn-panel{animation:cn-fade .3s ease both;}
@keyframes cn-fade{from{opacity:0;transform:translateY(6px);}to{opacity:1;transform:none;}}
`

let _cnStyleInjected = false
function CNStyle() {
  useEffect(() => {
    if (_cnStyleInjected) return
    const el = document.createElement('style')
    el.setAttribute('data-cn-style', '1')
    el.textContent = CN_CSS
    document.head.appendChild(el)
    _cnStyleInjected = true
  }, [])
  return null
}

const STORE_KEY = 'connections.activeTab'
const TABS = [
  { key: 'connectors', label: 'Connectors', icon: Plug,    Panel: SyncManager },
  { key: 'webhooks',   label: 'Webhooks',   icon: Webhook, Panel: Integrations },
  { key: 'adapters',   label: 'Adapters',   icon: Boxes,   Panel: AdapterManager },
]

export default function Connections() {
  const [active, setActive] = useState(() => {
    try {
      const saved = localStorage.getItem(STORE_KEY)
      if (saved && TABS.some(t => t.key === saved)) return saved
    } catch { /* ignore */ }
    return TABS[0].key
  })

  const select = (key) => {
    setActive(key)
    try { localStorage.setItem(STORE_KEY, key) } catch { /* ignore */ }
  }

  const current = TABS.find(t => t.key === active) || TABS[0]
  const ActivePanel = current.Panel

  return (
    <div className="cn-root space-y-6">
      <CNStyle />
      <div>
        <h2 className="cn-title">
          <Cable size={24} style={{ color: 'var(--cn-accent)' }} /> Connections
        </h2>
        <p className="cn-sub">Every system that feeds or receives your data, in one place.</p>
      </div>

      <div style={{ maxWidth: '42rem' }}><AgentAsk service="connector-health" agentName="Connector Health Agent" starters={['Which sources are stale or degraded?', 'What data gaps should I fix?']} /></div>

      <div className="cn-tabs" role="tablist">
        {TABS.map(t => {
          const Icon = t.icon
          const isActive = t.key === active
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={isActive}
              onClick={() => select(t.key)}
              className={`cn-tab${isActive ? ' active' : ''}`}
            >
              <Icon size={14} /> {t.label}
            </button>
          )
        })}
      </div>

      <div className="cn-panel" key={active} role="tabpanel">
        <ActivePanel />
      </div>
    </div>
  )
}
